<template>
  <div :class="section_class">
    <div class="rvr-collapsible-section-title" @click="open = !open">
      <strong>{{ t.setup_for_placenumber }} {{ id }}</strong>

      <!-- Place information -->
      <div class="place-information-wrapper">
        <span
            class="place-color-indicator"
            :style="{ backgroundColor: placeSize.color_code }"
        ></span>
        <span class="place-size">{{ placeSize.size }}</span>
        <span class="place-price">{{ formatPrice(parseFloat(placeSize.price)) }}</span>
      </div>


      <!-- Configuration status indicator -->
      <span :class="['configuration-status', isConfigured ? 'configured' : 'not-configured']">
        {{ isConfigured ? '✓' : 'x' }} {{ isConfigured ? t.configured : t.not_configured }}
      </span>

      <!-- Chevron icon -->
      <font-awesome-icon :icon="open ? 'angle-up' : 'angle-down'" />
    </div>

    <div class="rvr-collapsible-section-content" v-show="open">
      <slot v-if="firstRenderDone"></slot>
    </div>
  </div>
</template>

<script>
import { ref, watch, defineComponent } from 'vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import useTexts from "@/composables/useTexts";
import { useFormatPrice } from "@/composables/useUtilityFunctions";

library.add(faAngleDown, faAngleUp);

export default defineComponent({
  name: 'CollapsibleStandSection',
  components: {
    FontAwesomeIcon,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    placeSize: {
      type: Object,
      required: true,
    },
    section_class: {
      type: String,
      required: true,
    },
    isConfigured: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['collapse-status'],
  setup(props, { emit }) {
    const open = ref(false);
    const firstRenderDone = ref(false);
    const { getTextsForComponent } = useTexts();
    const t = getTextsForComponent('CollapsibleStandSection');
    const { formatPrice } = useFormatPrice();

    watch(open, (newStatus) => {
      emit('collapse-status', newStatus);
      if (newStatus && !firstRenderDone.value) {
        firstRenderDone.value = true;
      }
    });

    return {
      open,
      firstRenderDone,
      t,
      formatPrice,
    };
  },
});
</script>

<style scoped>
.rvr-collapsible-section-title,
.rvr-collapsible-section-content {
  padding: 20px;
}

.rvr-collapsible-section-title {
  border-bottom: 1px solid #e9e9e9;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.rvr-collapsible-section-title:hover {
  background-color: #f5f5f5;
}

.rvr-collapsible-section-title strong {
  font-size: 1.2em;
  margin-right: 20px;
}

.rvr-collapsible-section-title svg {
  margin-left: 20px;
  height: 25px;
}

.configuration-status {
  margin-left: 10px;
  padding: 4px 8px;
  font-size: 0.9em;
  font-weight: bold;
}

.configuration-status.configured {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.configuration-status.not-configured {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.place-information-wrapper {
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  margin-right: 15px;
}

.place-color-indicator {
  width: 20px;
  height: 20px;
  display: inline-block;
  margin-right: 5px;
  border-radius: 4px;
}

.place-size,
.place-price {
  vertical-align: middle;
  margin-left: 5px;
}

.place-price {
  font-style: italic;
  font-weight: bold;
}
</style>
